import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { api, errMsg, type Account, type Transfer, type TransferResult } from "../api";
import { fmtDay } from "../lib/money";
import { useMe } from "../session";
import { useTxFlow } from "../txflow";
import { HistoryModal } from "./HistoryModal";
import { Modal } from "./Modal";
import { RowMenu } from "./RowMenu";
import { Btn, Field, Input, Notice } from "./ui";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
type Dlg = "confirm" | "cancel" | "history" | null;
export const trTitle = (t: Transfer) => t.description || `Transferência ${t.fromAccountName} → ${t.toAccountName}`;

/** Ações de uma transferência: realizar, editar, voltar a planejada, cancelar, restaurar e histórico. */
/** `menu` (DEC-112): as mesmas ações dentro do menu ⋮; `extra` acrescenta itens da página (conciliação). Sem `menu`, o comportamento em linha é o de sempre. */
export function TransferActions({ tr, canWrite, onChanged, onMessage, menu, extra }: { tr: Transfer; canWrite: boolean; onChanged: () => void; onMessage: (m: { kind: "ok" | "error"; text: string }) => void; menu?: boolean; extra?: ReactNode }) {
  const { me } = useMe();
  const realm = me.user.realm;
  const flow = useTxFlow();
  const [dlg, setDlg] = useState<Dlg>(null);

  async function simple(action: "unconfirm" | "restore", ok: string) {
    try {
      const r = await api<TransferResult>("POST", `/api/${realm}/transfers/${tr.id}/${action}`, { version: tr.version });
      flow.showImpacts(r.impacts); onMessage({ kind: "ok", text: ok }); onChanged(); flow.bump();
    } catch (e) { onMessage({ kind: "error", text: errMsg(e) }); onChanged(); }
  }
  return (
    <>
      {menu ? <RowMenu label={trTitle(tr)}>
        {canWrite && tr.status === "planejado" && <button className="link" onClick={() => setDlg("confirm")} data-act="confirm">Marcar como realizada</button>}
        {canWrite && tr.status !== "cancelado" && <button className="link" onClick={() => flow.openTransfer(tr)} data-act="edit">Editar</button>}
        {canWrite && tr.status === "confirmado" && <button className="link" onClick={() => void simple("unconfirm", "Transferência voltou a planejada.")} data-act="unconfirm">Voltar a planejada</button>}
        {canWrite && tr.status !== "cancelado" && <button className="link danger" onClick={() => setDlg("cancel")} data-act="cancel">Excluir</button>}
        {canWrite && tr.status === "cancelado" && <button className="link" onClick={() => void simple("restore", "Transferência restaurada como planejada.")} data-act="restore">Restaurar</button>}
        {extra}
        <button className="link" onClick={() => setDlg("history")} data-act="history">Histórico</button>
      </RowMenu> : <span className="actions">
        {canWrite && tr.status === "planejado" && <button className="link" onClick={() => setDlg("confirm")} data-act="confirm">Marcar como realizada</button>}
        {canWrite && tr.status !== "cancelado" && <button className="link" onClick={() => flow.openTransfer(tr)} data-act="edit">Editar</button>}
        {canWrite && tr.status === "confirmado" && <button className="link" onClick={() => void simple("unconfirm", "Transferência voltou a planejada.")} data-act="unconfirm">Voltar a planejada</button>}
        {canWrite && tr.status !== "cancelado" && <button className="link danger" onClick={() => setDlg("cancel")} data-act="cancel">Excluir</button>}
        {canWrite && tr.status === "cancelado" && <button className="link" onClick={() => void simple("restore", "Transferência restaurada como planejada.")} data-act="restore">Restaurar</button>}
        {extra}
        <button className="link" onClick={() => setDlg("history")} data-act="history">Histórico</button>
      </span>}
      {dlg === "confirm" && <ConfirmDialog tr={tr} onClose={() => setDlg(null)} onDone={(r) => { setDlg(null); flow.showImpacts(r.impacts); onMessage({ kind: "ok", text: `${trTitle(tr)}: realizada.` }); onChanged(); flow.bump(); }} />}
      {dlg === "cancel" && <CancelDialog tr={tr} onClose={() => setDlg(null)} onDone={(r) => { setDlg(null); flow.showImpacts(r.impacts); onMessage({ kind: "ok", text: `${trTitle(tr)} cancelada. O histórico foi preservado.` }); onChanged(); flow.bump(); }} />}
      {dlg === "history" && <HistoryModal title={`Histórico — ${trTitle(tr)}`} path={`/api/${realm}/transfers/${tr.id}/history`} onClose={() => setDlg(null)} />}
    </>
  );
}

function ConfirmDialog({ tr, onClose, onDone }: { tr: Transfer; onClose: () => void; onDone: (r: TransferResult) => void }) {
  const { me } = useMe();
  const [date, setDate] = useState(today());
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // DEC-112: limite mínimo visível = saldo inicial mais recente entre as duas contas (a mesma regra do servidor)
  const [floor, setFloor] = useState<{ date: string; name: string } | null>(null);
  useEffect(() => {
    void api<Account[]>("GET", `/api/${me.user.realm}/accounts?entityId=${tr.entityId}`).then((all) => {
      const two = all.filter((a) => a.id === tr.fromAccountId || a.id === tr.toAccountId);
      const top = two.reduce<Account | null>((m, a) => (!m || a.initialBalanceDate > m.initialBalanceDate ? a : m), null);
      if (top) setFloor({ date: top.initialBalanceDate, name: top.name });
    }).catch(() => undefined);
  }, [me.user.realm, tr.entityId, tr.fromAccountId, tr.toAccountId]);
  async function submit(e: FormEvent) {
    e.preventDefault(); setBusy(true); setErr(null);
    try { onDone(await api<TransferResult>("POST", `/api/${me.user.realm}/transfers/${tr.id}/confirm`, { version: tr.version, settledDate: date })); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }
  return (
    <Modal title="Marcar como realizada" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}><strong>{trTitle(tr)}</strong> — informe a data em que foi realizada.</p>
        <Field label="Data efetiva"><Input type="date" value={date} min={floor?.date} max={today()} onChange={(e) => setDate(e.target.value)} name="settledDate" required /></Field>
        <p className="small muted" data-date-limits style={{ marginTop: -4 }}>Até hoje ({fmtDay(today())}): a data efetiva registra algo que já aconteceu; para uma data futura, mantenha a transferência como planejada.{floor && <> Não pode ser anterior a {fmtDay(floor.date)} (saldo inicial da conta «{floor.name}»).</>}</p>
        <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn primary" disabled={busy} data-confirm-save>{busy ? "Salvando…" : "Confirmar"}</button></div>
      </form>
    </Modal>
  );
}

function CancelDialog({ tr, onClose, onDone }: { tr: Transfer; onClose: () => void; onDone: (r: TransferResult) => void }) {
  const { me } = useMe();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function go(scope: "this" | "following") {
    setBusy(true); setErr(null);
    try { onDone(await api<TransferResult>("POST", `/api/${me.user.realm}/transfers/${tr.id}/cancel`, { version: tr.version, reason, ...(tr.seriesId && scope === "following" ? { scope } : {}) })); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  }
  const submit = (e: FormEvent) => { e.preventDefault(); if (!tr.seriesId) void go("this"); };
  return (
    <Modal title="Excluir transferência" onClose={onClose}>
      <form onSubmit={submit} noValidate>
        {err && <Notice kind="error">{err}</Notice>}
        <p style={{ marginTop: 0 }}>A transferência <strong>{trTitle(tr)}</strong> deixa de contar nos saldos das duas contas. Nada é apagado: ela continua no histórico e pode ser restaurada como planejada. Se o dinheiro já retornou de fato, registre uma nova transferência no sentido contrário.</p>
        <Field label="Motivo da exclusão"><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} required name="reason" autoComplete="off" /></Field>
        {tr.seriesId
          ? <>
              <p className="small muted" data-scope-box>{tr.seriesKind === "parcelada" ? `Parcela ${tr.installmentNo}/${tr.installmentTotal}` : "Transferência fixa"}: escolha o alcance da exclusão. As anteriores e as já realizadas nunca são alteradas{tr.seriesKind === "fixa" ? "; cancelar esta e as seguintes encerra a renovação da fixa" : ""}.</p>
              <div className="scope-choices">
                <Btn variant="ghost" onClick={onClose}>Voltar</Btn>
                <Btn onClick={() => void go("this")} disabled={busy || reason.trim().length < 3} data-cancel-this>Excluir somente esta</Btn>
                <Btn variant="danger" onClick={() => void go("following")} disabled={busy || reason.trim().length < 3} data-scope-following>Excluir esta e as seguintes</Btn>
              </div>
            </>
          : <div className="modal-actions"><Btn variant="ghost" onClick={onClose}>Voltar</Btn><button type="submit" className="btn primary" disabled={busy} data-cancel-save>{busy ? "Excluindo…" : "Excluir transferência"}</button></div>}
      </form>
    </Modal>
  );
}
